import { useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, Image as ImageIcon, Sparkles, Download, X, Building2, Users, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STAGE_LABEL = {
  starting: "Starting…",
  reading_image: "Reading company names from the image…",
  finding_contacts: "Finding real contacts via Apollo…",
  enriching: "Enriching emails & LinkedIn…",
  done: "Done",
};

export default function ContactBuilderPanel() {
  const [image, setImage] = useState(null);       // data URL for preview
  const [prompt, setPrompt] = useState("Find logistics / procurement decision-maker contacts for these companies");
  const [pasted, setPasted] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const fileRef = useRef(null);

  const onFile = (f) => {
    if (!f) return;
    if (!/(png|jpe?g|webp)$/i.test(f.type)) { toast.error("Use PNG, JPG or WEBP"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setImage(e.target.result);
    reader.readAsDataURL(f);
  };

  const build = async () => {
    const companies = pasted.split(/\r?\n|,|;|\t/).map((s) => s.trim()).filter(Boolean);
    if (!image && companies.length === 0) { toast.error("Add an image or paste company names"); return; }
    setBusy(true); setJob(null);
    try {
      const { data } = await api.post("/apollo/build-contacts", {
        image_base64: image || null, prompt, companies,
      });
      poll(data.job_id);
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || "unknown"}`);
      setBusy(false);
    }
  };

  const poll = (jobId) => {
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/apollo/build-contacts/${jobId}`);
        setJob(data);
        if (data.status === "done") {
          clearInterval(iv); setBusy(false);
          toast.success(`Built ${data.result?.prospects_created || 0} contacts`);
        } else if (data.status === "failed") {
          clearInterval(iv); setBusy(false);
          toast.error(data.error || "Build failed");
        }
      } catch { clearInterval(iv); setBusy(false); }
    }, 3000);
    setTimeout(() => clearInterval(iv), 240000);
  };

  const downloadCsv = () => {
    const csv = job?.result?.csv;
    if (!csv) return;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `contacts_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const r = job?.result;

  return (
    <>
      <div className="bg-signal-500/5 border border-signal-500/20 rounded-sm p-3 mb-6 flex gap-3" data-testid="cb-callout">
        <Sparkles className="w-4 h-4 text-signal-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-foreground/80 leading-relaxed">
          <strong className="text-signal-500">Drop a screenshot of a company list</strong> (or paste names) + a prompt.
          The AI reads the companies, finds real decision-maker contacts via Apollo (keeping any contact already in the image as a fallback),
          creates prospects, and gives you a CSV. Up to 50 companies per run.
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="space-y-4">
          <div className="data-card">
            <Label className="text-xs">Screenshot (PNG / JPG / WEBP)</Label>
            {image ? (
              <div className="relative mt-2">
                <img src={image} alt="upload" className="w-full rounded-sm border border-border max-h-72 object-contain bg-muted/20" data-testid="cb-image-preview" />
                <button onClick={() => { setImage(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="absolute top-2 right-2 w-7 h-7 bg-background/90 border border-border rounded-sm flex items-center justify-center hover:text-destructive" data-testid="cb-image-clear">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} data-testid="cb-upload-btn"
                className="mt-2 w-full border border-dashed border-border rounded-sm py-10 flex flex-col items-center gap-2 text-muted-foreground hover:border-signal-500/50 hover:text-foreground transition-colors">
                <Upload className="w-6 h-6" />
                <span className="text-xs">Click to upload a screenshot</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])} data-testid="cb-file-input" />
          </div>

          <div className="data-card">
            <Label className="text-xs">Prompt / instruction</Label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
              data-testid="cb-prompt-input"
              className="mt-2 w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500" />
          </div>

          <div className="data-card">
            <Label className="text-xs">…or paste company names (one per line)</Label>
            <textarea value={pasted} onChange={(e) => setPasted(e.target.value)} rows={4}
              data-testid="cb-paste-input" placeholder={"Maersk\nKuehne + Nagel\nDB Schenker"}
              className="mt-2 w-full px-3 py-2 text-sm font-mono bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500" />
          </div>

          <Button onClick={build} disabled={busy} data-testid="cb-build-btn" className="w-full rounded-sm">
            <Users className="w-3.5 h-3.5 mr-1.5" />{busy ? "Building…" : "Build contacts"}
          </Button>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {busy && job && (
            <div className="data-card" data-testid="cb-progress">
              <div className="text-sm font-medium mb-2 inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-signal-500 animate-pulse" /> {STAGE_LABEL[job.stage] || "Working…"}
              </div>
              {job.total > 0 && (
                <>
                  <div className="h-1.5 bg-muted/40 rounded-full overflow-hidden">
                    <div className="h-full bg-signal-500 transition-all" style={{ width: `${Math.round((job.processed / job.total) * 100)}%` }} />
                  </div>
                  <div className="text-[0.65rem] text-muted-foreground mt-1">{job.processed} / {job.total} companies</div>
                </>
              )}
            </div>
          )}

          {job?.status === "failed" && (
            <div className="data-card border-destructive/40 text-sm inline-flex items-center gap-2" data-testid="cb-error">
              <AlertCircle className="w-4 h-4 text-destructive" /> {job.error}
            </div>
          )}

          {r && (
            <>
              <div className="data-card" data-testid="cb-summary">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold">Built {r.prospects_created} contacts</div>
                  <Button size="sm" onClick={downloadCsv} disabled={!r.csv} data-testid="cb-download-csv" className="rounded-sm">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Stat label="Companies" value={r.companies_processed} />
                  <Stat label="Contacts found" value={r.contacts_found} />
                  <Stat label="Emails/LinkedIn enriched" value={r.enriched} />
                  <Stat label="Front-door emails sent" value={r.routing_emails_sent} />
                </div>
                {!r.apollo_available && (
                  <div className="text-[0.65rem] text-amber-accent mt-2 inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Apollo not configured — used image data only.
                  </div>
                )}
              </div>

              <div className="data-card max-h-[28rem] overflow-y-auto" data-testid="cb-results-table">
                {(r.rows || []).map((p, i) => (
                  <div key={i} className="py-2 border-b border-border/60 last:border-0">
                    <div className="text-sm font-medium">{p.full_name !== p.company ? p.full_name : p.company}</div>
                    {p.title && <div className="text-[0.65rem] text-muted-foreground">{p.title}</div>}
                    <div className="text-[0.65rem] text-signal-500 inline-flex items-center gap-1"><Building2 className="w-2.5 h-2.5" /> {p.company}</div>
                    <div className="text-[0.6rem] text-muted-foreground truncate">{p.email || "no email"}{p.location ? ` · ${p.location}` : ""}</div>
                  </div>
                ))}
                {(r.rows || []).length === 0 && <div className="text-xs text-muted-foreground text-center py-6">No contacts created.</div>}
              </div>
            </>
          )}

          {!busy && !job && (
            <div className="data-card text-center py-16 text-muted-foreground">
              <ImageIcon className="w-8 h-8 mx-auto mb-2" />
              <p className="text-xs">Results and CSV will appear here.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-muted/20 border border-border rounded-sm p-2">
      <div className="text-lg font-display font-semibold">{value ?? 0}</div>
      <div className="text-[0.6rem] tracking-widest uppercase text-muted-foreground">{label}</div>
    </div>
  );
}
